import React, { useState } from 'react';
import { isEmpty } from 'lodash';

import { useStyles } from '~/assets/styles';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import Link from '~/components/core/Atomic/Link/Link';
import MenuItem from '~/components/core/Atomic/MenuItem';
import DatePickerField, { type PickerDate } from '~/components/core/Molecules/Fields/DatePickerField';
import MultiSelectField from '~/components/core/Molecules/Fields/MultiSelectField';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import TextField from '~/components/core/Molecules/Fields/TextField';
import PermissionsButtonWrapper from '~/components/core/Permissions/PermissionsButtonWrapper';
import CancelIcon from '~/components/icons/CancelIcon';
import cn from '~/Utils/cn';

import type { SorIssuesFilters as Filters } from '../hooks/useSorIssues';
import TEXTS from '../texts';
import type { SorIssueStatusCounts, SorOperation } from '../types';
import { SorIssueFilterStatus } from '../types';

const STATUSES = Object.values(SorIssueFilterStatus);

// A cleared picker clears the filter; a day typed half-way is no filter yet, and the picker keeps its text.
const toLocalDay = (date: PickerDate): string | null => {
  if (!date) {
    return '';
  }
  return date.isValid() ? date.format('YYYY-MM-DD') : null;
};

interface SorIssuesFiltersProps {
  filters: Filters;
  operations: SorOperation[];
  statusCounts: SorIssueStatusCounts['status_counts'] | undefined;
  isReconciling: boolean;
  onFiltersChange: (update: Partial<Filters>) => void;
  onClearFilters: () => void;
  onReconcile: () => void;
}

const SorIssuesFilters: React.FC<SorIssuesFiltersProps> = ({
  filters,
  operations,
  statusCounts,
  isReconciling,
  onFiltersChange,
  onClearFilters,
  onReconcile,
}) => {
  const { classes } = useStyles();
  // The server matches a claim number exactly, so it filters once entered rather than on every keystroke.
  const [claimNumber, setClaimNumber] = useState(filters.claimNumber);

  const applyClaimNumber = () => {
    const trimmed = claimNumber.trim();
    if (trimmed !== filters.claimNumber) {
      onFiltersChange({ claimNumber: trimmed });
    }
  };

  const handleDateChange = (field: 'startDate' | 'endDate') => (date: PickerDate) => {
    const localDay = toLocalDay(date);
    if (localDay !== null) {
      onFiltersChange({ [field]: localDay });
    }
  };

  const handleClear = () => {
    setClaimNumber('');
    onClearFilters();
  };

  return (
    <div className="flex flex-wrap items-end gap-20">
      <div className="w-[260px]">
        <MultiSelectField
          id="sor-issue-status"
          label={TEXTS.issues.status}
          className={classes.textField}
          fullWidth
          options={STATUSES}
          value={filters.status}
          onChange={(status) => onFiltersChange({ status })}
          autoSelectSingleOption={false}
          renderValue={(selected) =>
            isEmpty(selected)
              ? TEXTS.issues.allStatuses
              : selected.map((status) => TEXTS.issueStatuses[status]).join(', ')
          }
          renderOption={(status) => (
            <div className="flex w-full items-center justify-between">
              <span>{TEXTS.issueStatuses[status]}</span>
              {statusCounts && (
                <span className="px-6 py-1 ml-8 min-w-[24px] rounded-full bg-slate-200 text-center text-xs font-medium text-slate-700">
                  {statusCounts[status]}
                </span>
              )}
            </div>
          )}
        />
      </div>
      <div className="w-[200px]">
        <SingleSelectField<string>
          id="sor-issue-operation"
          label={TEXTS.issues.operation}
          className={classes.textField}
          fullWidth
          value={filters.operation}
          onChange={(operation) => onFiltersChange({ operation })}
          clearable
          clearSelection={() => onFiltersChange({ operation: '' })}
          autoSelectSingleOption={false}
        >
          {operations.map(({ operation, label }) => (
            <MenuItem key={operation} value={operation}>
              {label}
            </MenuItem>
          ))}
        </SingleSelectField>
      </div>
      <div className="w-[180px]">
        <TextField<string>
          id="sor-issue-claim-number"
          label={TEXTS.issues.claimNumber}
          className={classes.textField}
          fullWidth
          value={claimNumber}
          onChange={setClaimNumber}
          onBlur={applyClaimNumber}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              applyClaimNumber();
            }
          }}
        />
      </div>
      <div className="w-[170px]">
        <DatePickerField
          id="sor-issue-from"
          label={TEXTS.issues.from}
          className={classes.textField}
          fullWidth
          clearable
          disableFuture
          value={filters.startDate}
          onChange={handleDateChange('startDate')}
        />
      </div>
      <div className="w-[170px]">
        <DatePickerField
          id="sor-issue-to"
          label={TEXTS.issues.to}
          className={classes.textField}
          fullWidth
          clearable
          disableFuture
          value={filters.endDate}
          onChange={handleDateChange('endDate')}
        />
      </div>
      <Link
        href=""
        onClick={(event: React.MouseEvent) => {
          event.preventDefault();
          handleClear();
        }}
        className={cn(classes.secondaryLink, 'pb-8')}
      >
        <CancelIcon className={cn(classes.filledIcon, classes.leftButtonIcon)} />
        {TEXTS.issues.clearFilters}
      </Link>
      <div className="ml-auto">
        <PermissionsButtonWrapper action={PERMISSION_ACTIONS.SOR_ISSUES_MANAGE} verb={PERMISSION_VERBS.WRITE}>
          <Button variant="contained" color="primary" disabled={isReconciling} onClick={onReconcile}>
            {TEXTS.issues.reconcileNow}
          </Button>
        </PermissionsButtonWrapper>
      </div>
    </div>
  );
};

export default SorIssuesFilters;
